// A ticket is a beast, and it loses a horn at every stage of the fight.
//
// The sword and the shield were a status marker BOLTED ON: a separate object
// standing in front of a piece, carrying one bit each. This makes the status
// the SHAPE OF THE PIECE instead - a beast with a full crown of horns is
// untouched work, and one down to its last is "it's only a flesh wound".
//
// WHY HORNS, AND WHY THE TALLEST GOES FIRST
//
// A piece is about eight screen pixels wide. Limbs spaced along a 1.1-unit
// body would be under two pixels apart and the COUNT would be unreadable at
// the size the board is actually used. Measured: legs alternating left and
// right do not even narrow the silhouette monotonically, because losing one
// on the left changes nothing until its opposite goes too.
//
// So the count is carried by HEIGHT. The horns are a dorsal ridge of
// decreasing length, and they are lost from the TALLEST down, so the beast's
// top edge falls at every stage. Height is the most legible dimension there
// is at this size - it survives being eight pixels wide - and it is the one
// that reads from across a room.
//
// Built with primitives and MERGED, so a beast is still one mesh and the
// draw-call cost is unchanged.

import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';

/** A beast starts with five horns and loses one at each stage of the fight.
 *  To Do has all five; Acceptance Testing has one. */
export const MAX_APPENDAGES = 5;

/**
 * Horn lengths, tallest first, as a fraction of the body height.
 *
 * The first one lost is the TALLEST, which is what makes the loss visible.
 *
 * These are much longer than they first were, and the reason is measured
 * rather than aesthetic. At the first lengths the silhouette fell from 9.8
 * to 6.4 screen pixels across four stages - under a pixel per stage, which
 * is not a signal, it is noise. Height is the one dimension this piece can
 * spend freely: unlike width it cannot make two neighbouring pieces touch,
 * so the horns can be long enough to be read.
 */
const HORN_RATIOS = [2.30, 1.82, 1.36, 0.92, 0.50] as const;

/**
 * One merged beast geometry carrying `appendages` horns, centred on its own
 * middle and sized against the piece size it will stand in for.
 *
 * Zero appendages is a plain body, which is what a vanquished beast is.
 */
export function buildBeastGeometry(appendages: number, mobSize: number): THREE.BufferGeometry {
  const S = mobSize;
  const n = Math.max(0, Math.min(MAX_APPENDAGES, Math.round(appendages)));

  const parts: THREE.BufferGeometry[] = [];
  const put = (g: THREE.BufferGeometry, x: number, y: number, z = 0) => {
    g.translate(x, y, z);
    parts.push(g);
  };

  // The body: low and long, so it reads as a creature rather than a crate.
  const bodyH = S * 0.58;
  const bodyW = S * 0.84;
  const bodyD = S * 1.02;
  put(new THREE.BoxGeometry(bodyW, bodyH, bodyD), 0, bodyH / 2);

  // A head at the front, so there is a way round to face.
  put(new THREE.BoxGeometry(S * 0.50, S * 0.44, S * 0.40), 0, bodyH * 0.84, bodyD / 2 + S * 0.17);

  // Legs, FIXED - four always. They are what makes this a beast rather than
  // a brick, and they are deliberately not the thing that changes, because
  // they cannot change legibly.
  for (const sx of [-1, 1]) {
    for (const sz of [-1, 1]) {
      put(
        new THREE.BoxGeometry(S * 0.16, S * 0.26, S * 0.16),
        sx * bodyW * 0.34,
        bodyH * 0.18,
        sz * bodyD * 0.30,
      );
    }
  }

  // The ridge. Horns are kept from the SHORTEST end, so the tallest is the
  // first to go and the silhouette falls at every stage. They lean back,
  // which is what stops them reading as a row of chimneys.
  const kept = HORN_RATIOS.slice(MAX_APPENDAGES - n);
  const ridge = S * 0.30;
  kept.forEach((ratio, i) => {
    const len = bodyH * ratio;
    const z = (i - (kept.length - 1) / 2) * S * 0.22;
    const horn = new THREE.CylinderGeometry(S * 0.045, S * 0.075, len, 5);
    horn.rotateX(-0.30);
    put(horn, 0, bodyH + len / 2 - S * 0.04, z);
    void ridge;
  });

  const merged = mergeGeometries(parts, false) ?? parts[0]!;
  for (const p of parts) if (p !== merged) p.dispose();
  // Centred on its own middle: mesh.position.y is computed downstream as
  // half the piece height, so an off-centre geometry floats or buries.
  merged.computeBoundingBox();
  const bb = merged.boundingBox!;
  merged.translate(0, -(bb.min.y + bb.max.y) / 2, 0);
  return merged;
}

/** How tall a beast with this many horns stands, for tests and for anyone
 *  who needs to know whether two pieces can overlap. */
export function beastHeight(appendages: number, mobSize: number): number {
  const g = buildBeastGeometry(appendages, mobSize);
  g.computeBoundingBox();
  const h = g.boundingBox!.max.y - g.boundingBox!.min.y;
  g.dispose();
  return h;
}
